'use client';
import { useState } from 'react';
import Link from 'next/link';
import { ShareBar } from '@/components/shared/ShareBar';
import { trackLead } from '@/lib/analytics';

function EmailCapture({ category, label }: { category: string; label: string }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState(false);
  // Fangst-broen: poster til smakebit-magneten (lead_downloads + full drip)
  // i stedet for døde newsletter_signups. utm_source gjør flaten målbar.
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !consent) return;
    setError(false);
    try {
      const res = await fetch('/api/smakebit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent: true, utm: { source: `kalkulator:${category}` } }),
      });
      if (!res.ok) { setError(true); return; }
    } catch { setError(true); return; }
    trackLead('generate_lead', { content_name: category, method: 'kalkulator' });
    setDone(true);
  }
  if (done) return (
    <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center mb-6">
      <div className="text-green-800 font-medium mb-1">Ukesmenyen er på vei!</div>
      <div className="text-green-600 text-sm">Sjekk innboksen — kanskje også søppelposten første gang. Du får også tips om {label.toLowerCase()} fremover.</div>
    </div>
  );
  return (
    <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 mb-6">
      <h3 className="font-semibold text-stone-900 mb-1">Få 7 middager under 500 kcal — gratis</h3>
      <p className="text-stone-500 text-sm mb-4">Ferdig ukesmeny som PDF med handleliste og næringsinnhold — pluss evidensbaserte tips om {label.toLowerCase()} (maks 1–2 e-poster i uka).</p>
      {error && <p className="text-red-600 text-xs mb-3">Noe gikk galt — prøv igjen om et øyeblikk.</p>}
      <form onSubmit={submit} className="space-y-3">
        <input type="email" placeholder="din@epost.no" value={email} onChange={e => setEmail(e.target.value)}
          className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400" />
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5 flex-shrink-0" />
          <span className="text-xs text-stone-500">Jeg samtykker til å motta markedsføring fra CoachGuiden per e-post. Du kan melde deg av når som helst.</span>
        </label>
        <button type="submit" disabled={!email || !consent} className="w-full py-2.5 bg-stone-900 text-white rounded-xl text-sm font-medium hover:bg-stone-800 disabled:opacity-40 transition-colors">
          Meld meg på gratis tips
        </button>
      </form>
    </div>
  );
}

function BMIGauge({ bmi }: { bmi: number }) {
  const clamp = Math.max(15, Math.min(40, bmi));
  const pct = ((clamp - 15) / 25) * 100;
  const color = bmi < 18.5 ? '#3b82f6' : bmi < 25 ? '#22c55e' : bmi < 30 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative h-4 bg-gradient-to-r from-blue-300 via-green-400 via-yellow-400 to-red-500 rounded-full overflow-hidden">
      <div className="absolute top-0 bottom-0 w-1 bg-stone-900 rounded-full shadow-lg transition-all duration-500" style={{ left: `calc(${pct}% - 2px)` }} />
    </div>
  );
}

export default function BMIKalkulator() {
  const [height, setHeight] = useState(175);
  const [weight, setWeight] = useState(75);
  const [age, setAge] = useState(30);
  const [gender, setGender] = useState('mann');
  const [unit, setUnit] = useState('metric');

  const heightCm = unit === 'imperial' ? Math.round(height * 2.54) : height;
  const weightKg = unit === 'imperial' ? Math.round(weight * 0.453592) : weight;

  const bmi = +(weightKg / ((heightCm / 100) ** 2)).toFixed(1);

  const category =
    bmi < 16 ? { label: 'Alvorlig undervekt', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' }
    : bmi < 18.5 ? { label: 'Undervekt', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' }
    : bmi < 25 ? { label: 'Normalvekt', color: 'text-green-700', bg: 'bg-green-50 border-green-200' }
    : bmi < 30 ? { label: 'Overvekt', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' }
    : bmi < 35 ? { label: 'Fedme klasse I', color: 'text-orange-700', bg: 'bg-orange-50 border-orange-200' }
    : bmi < 40 ? { label: 'Fedme klasse II', color: 'text-red-700', bg: 'bg-red-50 border-red-200' }
    : { label: 'Fedme klasse III', color: 'text-red-800', bg: 'bg-red-50 border-red-200' };

  const idealMin = +(18.5 * ((heightCm / 100) ** 2)).toFixed(1);
  const idealMax = +(24.9 * ((heightCm / 100) ** 2)).toFixed(1);
  const diffMin = +(weightKg - idealMin).toFixed(1);
  const diffMax = +(weightKg - idealMax).toFixed(1);

  return (
    <div className="min-h-screen gradient-warm">
      {/* Nav */}
      <header className="glass border-b border-stone-200/50 sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 font-semibold text-stone-900">
            <span className="w-7 h-7 rounded-lg bg-stone-900 flex items-center justify-center"><svg viewBox="0 0 32 32" fill="none" className="w-5 h-5"><circle cx="16" cy="16" r="11" stroke="#44403c" strokeWidth="1"/><circle cx="16" cy="16" r="7.5" stroke="#57534e" strokeWidth="0.7"/><polygon points="16,6 17.5,13 16,12 14.5,13" fill="#ffffff"/><polygon points="16,26 14.5,19 16,20 17.5,19" fill="#78716c"/><path d="M16,5 A11,11 0 1,0 16,27" stroke="#f5f5f4" strokeWidth="1.8" strokeLinecap="round" fill="none"/></svg></span>
            CoachGuiden
          </Link>
          <nav className="hidden sm:flex items-center gap-4 text-sm text-stone-500">
            <Link href="/ressurser" className="hover:text-stone-900">← Alle kalkulatorer</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Breadcrumb */}
        <nav className="text-xs text-stone-400 mb-6 flex items-center gap-1">
          <Link href="/" className="hover:text-stone-600">Hjem</Link>
          <span>›</span>
          <Link href="/ressurser" className="hover:text-stone-600">Ressurser</Link>
          <span>›</span>
          <span className="text-stone-600">BMI-kalkulator</span>
        </nav>

        <h1 className="text-3xl font-semibold text-stone-900 mb-2">BMI-kalkulator</h1>
        <p className="text-stone-500 mb-6 leading-relaxed">Beregn din kroppsmasseindeks (BMI) og finn ut hva det betyr for helsen din. Oppdateres automatisk mens du skriver.</p>
        <div className="rounded-3xl overflow-hidden shadow-xl shadow-stone-200/60 aspect-[16/9] mb-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/tools/bmi-kalkulator.jpg" alt="BMI-kalkulator" className="w-full h-full object-cover" loading="eager"/>
        </div>

        {/* Unit toggle */}
        <div className="flex gap-2 mb-6">
          {[['metric','Metrisk (kg/cm)'],['imperial','Imperisk (lbs/in)']].map(([v,l]: any) => (
            <button key={v} onClick={() => setUnit(v)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${unit === v ? 'bg-stone-900 text-white' : 'bg-white border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>{l}</button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          {/* Inputs */}
          <div className="bg-white border border-stone-200 rounded-2xl p-6 space-y-5">
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-stone-700">Høyde</label>
                <span className="text-sm font-semibold text-stone-900">{height} {unit === 'metric' ? 'cm' : 'in'}</span>
              </div>
              <input type="range" min={unit === 'metric' ? 140 : 55} max={unit === 'metric' ? 220 : 87} value={height} onChange={e => setHeight(+e.target.value)} className="w-full accent-stone-900" />
              <div className="flex justify-between text-xs text-stone-400 mt-1">
                <span>{unit === 'metric' ? '140 cm' : '55 in'}</span>
                <span>{unit === 'metric' ? '220 cm' : '87 in'}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-stone-700">Vekt</label>
                <span className="text-sm font-semibold text-stone-900">{weight} {unit === 'metric' ? 'kg' : 'lbs'}</span>
              </div>
              <input type="range" min={unit === 'metric' ? 40 : 88} max={unit === 'metric' ? 200 : 440} value={weight} onChange={e => setWeight(+e.target.value)} className="w-full accent-stone-900" />
              <div className="flex justify-between text-xs text-stone-400 mt-1">
                <span>{unit === 'metric' ? '40 kg' : '88 lbs'}</span>
                <span>{unit === 'metric' ? '200 kg' : '440 lbs'}</span>
              </div>
            </div>
            <div>
              <div className="flex justify-between mb-2">
                <label className="text-sm font-medium text-stone-700">Alder</label>
                <span className="text-sm font-semibold text-stone-900">{age} år</span>
              </div>
              <input type="range" min={18} max={100} value={age} onChange={e => setAge(+e.target.value)} className="w-full accent-stone-900" />
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-2">Kjønn</label>
              <div className="flex gap-2">
                {[['mann','Mann'],['kvinne','Kvinne']].map(([v,l]: any) => (
                  <button key={v} onClick={() => setGender(v)} className={`flex-1 py-2 rounded-xl text-sm font-medium transition-colors ${gender === v ? 'bg-stone-900 text-white' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="space-y-4">
            <div className={`border rounded-2xl p-6 text-center ${category.bg}`}>
              <div className="text-6xl font-semibold text-stone-900 mb-1">{bmi}</div>
              <div className={`text-lg font-medium mb-4 ${category.color}`}>{category.label}</div>
              <BMIGauge bmi={bmi} />
              <div className="flex justify-between text-xs text-stone-400 mt-1">
                <span>16</span><span>18.5</span><span>25</span><span>30</span><span>40</span>
              </div>
            </div>
            <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
              <h3 className="text-sm font-semibold text-stone-900">Idealvekt for din høyde</h3>
              <div className="flex justify-between text-sm">
                <span className="text-stone-500">Normalvekt-spekter</span>
                <span className="font-medium text-stone-900">{idealMin} – {idealMax} kg</span>
              </div>
              {bmi >= 18.5 && bmi < 25 ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-xs text-green-700">✓ Du er innenfor normalvektområdet</div>
              ) : bmi < 18.5 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700">Du er {Math.abs(diffMin).toFixed(1)} kg under nedre grense for normalvekt</div>
              ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">Du er {Math.abs(diffMax).toFixed(1)} kg over normalvektområdet</div>
              )}
            </div>
          </div>
        </div>

        {/* BMI table */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">BMI-kategorier (WHO)</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-200"><th className="text-left py-2 text-stone-500 font-medium">Kategori</th><th className="text-right py-2 text-stone-500 font-medium">BMI</th><th className="py-2"></th></tr></thead>
              <tbody>
                {[
                  ['Alvorlig undervekt','< 16','blue'],
                  ['Undervekt','16 – 18.4','blue'],
                  ['Normalvekt','18.5 – 24.9','green'],
                  ['Overvekt','25 – 29.9','amber'],
                  ['Fedme klasse I','30 – 34.9','orange'],
                  ['Fedme klasse II','35 – 39.9','red'],
                  ['Fedme klasse III','≥ 40','red'],
                ].map(([cat, range, col]: any) => (
                  <tr key={cat} className={`border-b border-stone-50 ${bmi >= parseFloat(range) || (range.startsWith('<') && bmi < 16) ? 'bg-stone-50' : ''}`}>
                    <td className="py-2.5 text-stone-700">{cat}</td>
                    <td className="py-2.5 text-right text-stone-900 font-mono">{range}</td>
                    <td className="py-2.5 pl-3"><div className={`w-3 h-3 rounded-full bg-${col}-400 mx-auto`}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Source/explanation box */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6">
          <h2 className="font-semibold text-stone-900 mb-4">Kilde og beregningsmetode</h2>
          <div className="space-y-3 text-sm text-stone-600">
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Formel</span><span>BMI = vekt (kg) ÷ høyde² (m). Eksempel: 75 kg og 1,75 m → 75 ÷ (1,75 × 1,75) = 24,5</span></div>
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Klassifisering</span><span>WHO International Classification of adult underweight, overweight and obesity, 2004. Oppdatert grenseverdi for Øst-Asia: overvekt fra BMI 23.</span></div>
            <div className="flex gap-3"><span className="text-stone-400 w-28 flex-shrink-0">Kilde</span><span><a href="https://www.who.int/news-room/fact-sheets/detail/obesity-and-overweight" target="_blank" rel="noopener" className="text-blue-600 hover:underline">WHO Obesity fact sheet →</a> og Helsedirektoratets retningslinjer for behandling av overvekt.</span></div>
            <div className="bg-stone-50 rounded-xl p-3 text-xs text-stone-500">BMI er et screening-verktøy, ikke en diagnose. Den tar ikke hensyn til muskelmasse, beinmasse, kjønn eller alder. En idrettsutøver kan ha høy BMI uten overvekt. Snakk med lege for individuell vurdering.</div>
          </div>
        </div>

        {/* Internal links */}
        <div className="bg-stone-50 border border-stone-200 rounded-2xl p-5 mb-6">
          <h3 className="font-medium text-stone-900 mb-3">Relaterte ressurser</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { href: "/ressurser/kalorikalkulator", title: "Kalorikalkulator", desc: "Beregn daglig kaloribehov", color: "bg-green-50 border-green-200 text-green-700" },
              { href: "/trening", title: "Finn personlig trener", desc: "Match med PT tilpasset dine mål", color: "bg-orange-50 border-orange-200 text-orange-700" },
              { href: "/kosthold", title: "Finn ernæringsfysiolog", desc: "Profesjonell kostholdsrådgivning", color: "bg-green-50 border-green-200 text-green-700" },
              { href: "/ressurser/artikler/ga-ned-i-vekt-uten-diett", title: "Gå ned i vekt uten å sulte", desc: "Les vår artikkel om varig vektreduksjon", color: "bg-stone-100 border-stone-200 text-stone-700" },
            ].map(l => (
              <a key={l.href} href={l.href} className={"flex items-center gap-3 border rounded-xl p-3 hover:shadow-sm transition-all " + l.color}>
                <div><div className="text-sm font-medium">{l.title}</div><div className="text-xs opacity-70">{l.desc}</div></div>
                <span className="ml-auto text-xs">→</span>
              </a>
            ))}
          </div>
        </div>

        {/* SEO content — utvidet guide for å passere Googles indekserings-terskel */}
        <article className="prose prose-stone max-w-none mb-10 space-y-8 text-stone-700 text-[15px] leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3 mt-0">Hva er BMI, egentlig?</h2>
            <p>BMI står for Body Mass Index — kroppsmasseindeks på norsk. Det er et matematisk forhold mellom høyde og vekt som ble utviklet av den belgiske matematikeren Adolphe Quetelet på 1830-tallet. Quetelets formål var ikke å vurdere individuell helse, men å beskrive statistiske mønstre i befolkningen. Først på 1970-tallet ble formelen tatt i bruk i klinisk medisin etter at den amerikanske fysiologen Ancel Keys publiserte en sammenlignende studie som viste at BMI korrelerte rimelig godt med kroppsfett på populasjonsnivå.</p>
            <p>I dag bruker Verdens helseorganisasjon (WHO) BMI som et globalt screening-verktøy for å klassifisere undervekt, normalvekt, overvekt og fedme hos voksne. Helsedirektoratet bruker samme kategorier i Norge. Det er viktig å understreke ordet <em>screening</em>: BMI flagger personer som kan trenge nærmere helsesjekk, men det er ikke en diagnose i seg selv.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Slik beregner du BMI manuelt</h2>
            <p>Formelen er enkel: <strong>BMI = vekt (kg) ÷ høyde² (m)</strong>. Du tar høyden i meter, ganger den med seg selv, og deler vekten på resultatet.</p>
            <p>Eksempel for en person som veier 78 kg og er 180 cm høy:</p>
            <ul className="list-disc pl-6">
              <li>Høyde i meter: 1,80</li>
              <li>Høyde² (1,80 × 1,80) = 3,24</li>
              <li>BMI = 78 ÷ 3,24 = <strong>24,1</strong></li>
            </ul>
            <p>For amerikanske enheter er formelen: BMI = (vekt i pund × 703) ÷ høyde i tommer². Kalkulatoren over gjør konverteringen automatisk hvis du bytter til "Imperial" (lb/ft + in).</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Full WHO-klassifisering med helserisiko</h2>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-stone-200 text-stone-500 text-xs uppercase tracking-wider">
                    <th className="text-left py-2.5 px-2 font-medium">Kategori</th>
                    <th className="text-left py-2.5 px-2 font-medium">BMI</th>
                    <th className="text-left py-2.5 px-2 font-medium">Helserisiko</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-stone-100"><td className="py-2.5 px-2">Alvorlig undervekt</td><td className="py-2.5 px-2 font-mono">&lt; 16</td><td className="py-2.5 px-2">Høy — næringsmangel, immunsvikt</td></tr>
                  <tr className="border-b border-stone-100"><td className="py-2.5 px-2">Undervekt</td><td className="py-2.5 px-2 font-mono">16 – 18,4</td><td className="py-2.5 px-2">Moderat — risiko for osteoporose, fertilitet</td></tr>
                  <tr className="border-b border-stone-100"><td className="py-2.5 px-2">Normalvekt</td><td className="py-2.5 px-2 font-mono">18,5 – 24,9</td><td className="py-2.5 px-2">Lav</td></tr>
                  <tr className="border-b border-stone-100"><td className="py-2.5 px-2">Overvekt</td><td className="py-2.5 px-2 font-mono">25 – 29,9</td><td className="py-2.5 px-2">Lett økt — type 2-diabetes, hjerte-kar</td></tr>
                  <tr className="border-b border-stone-100"><td className="py-2.5 px-2">Fedme klasse I</td><td className="py-2.5 px-2 font-mono">30 – 34,9</td><td className="py-2.5 px-2">Moderat økt</td></tr>
                  <tr className="border-b border-stone-100"><td className="py-2.5 px-2">Fedme klasse II</td><td className="py-2.5 px-2 font-mono">35 – 39,9</td><td className="py-2.5 px-2">Høy — bariatriske behandlingsalternativer vurderes</td></tr>
                  <tr><td className="py-2.5 px-2">Fedme klasse III</td><td className="py-2.5 px-2 font-mono">≥ 40</td><td className="py-2.5 px-2">Svært høy — sterk anbefaling om medisinsk behandling</td></tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-stone-500 mt-3">For voksne av østasiatisk etnisitet anbefaler WHO lavere terskler: overvekt fra BMI 23 og fedme fra BMI 27,5, fordi populasjonsdata viser økt metabolsk risiko ved lavere BMI-verdier.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Begrensninger — fem situasjoner der BMI gir feil bilde</h2>
            <p>BMI er en svært grov modell. Den behandler 1 kg fett og 1 kg muskel som det samme, og det er problematisk fordi muskel og fett har helt ulik metabolsk effekt på kroppen. Her er de fem viktigste situasjonene der BMI bommer:</p>
            <ol className="list-decimal pl-6 space-y-2">
              <li><strong>Atleter og styrketrenere.</strong> En rugbyspiller på 95 kg med 12 % kroppsfett og en stillesittende person på 95 kg med 32 % kroppsfett kan ha helt lik BMI, men helsetilstanden er ikke i samme univers.</li>
              <li><strong>Eldre.</strong> Med alderen mister man muskelmasse (sarkopeni). En 70-åring med "normal" BMI kan ha lite muskel og mye fett — det som kalles <em>sarkopen fedme</em> — og en høyere helserisiko enn BMI antyder.</li>
              <li><strong>Etnisitet.</strong> Asiatiske populasjoner har høyere andel kroppsfett ved samme BMI sammenlignet med europeere. Personer med afrikansk opphav har ofte mer muskel og tetter bein, og overestimeres dermed av BMI.</li>
              <li><strong>Kjønn.</strong> Kvinner har naturlig høyere fettandel enn menn ved samme BMI, men dette fanger ikke BMI opp.</li>
              <li><strong>Graviditet og amming.</strong> BMI er ikke validert for gravide. Bruk Helsedirektoratets vekt-anbefalinger basert på BMI før graviditet i stedet.</li>
            </ol>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Når bør du ikke bruke BMI alene</h2>
            <p>Du bør ikke basere viktige helsebeslutninger på BMI alene hvis du:</p>
            <ul className="list-disc pl-6">
              <li>Trener systematisk styrke og har målbart over gjennomsnittlig muskelmasse</li>
              <li>Er gravid eller ammer</li>
              <li>Er under 18 år (bruk barne-BMI-percentiler i stedet, beregnes via Helsedirektoratet)</li>
              <li>Er over 65 år og lurer på om du har sarkopeni</li>
              <li>Har hatt store vektendringer siste 6 måneder</li>
              <li>Har en kronisk sykdom (kreft, hjertesvikt, KOLS) som påvirker vekten</li>
            </ul>
            <p>I disse tilfellene bør du kombinere BMI med ett eller flere tilleggsmål.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Bedre alternativer enn BMI alene</h2>
            <p>De fleste eksperter, inkludert American College of Sports Medicine, anbefaler å kombinere BMI med minst ett kroppssammensetnings-mål. De fire mest brukte:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Midjemål.</strong> Måles på det smaleste punktet mellom nederste ribbein og hoftekam. Risikoterskel: &gt; 102 cm for menn, &gt; 88 cm for kvinner. Korrelerer sterkere med metabolsk syndrom enn BMI.</li>
              <li><strong>Midje-til-høyde-forhold (WHtR).</strong> Midjemål delt på høyde. Tommelfingerregelen er at midjen ikke bør overstige halvparten av høyden. En studie publisert i Obesity Reviews (Ashwell, 2012) viste at WHtR predikerer kardiovaskulær risiko bedre enn BMI på tvers av etnisitet og kjønn.</li>
              <li><strong>Midje-til-hofte-forhold (WHR).</strong> WHO anbefaler &lt; 0,90 for menn og &lt; 0,85 for kvinner. Indikerer hvor mye av fettet som ligger i mageregionen (visceralt fett), som er den helseskadelige typen.</li>
              <li><strong>Kroppsfett-prosent.</strong> Måles med DEXA-scan (gullstandard), bioimpedans-vekt eller hudfold-tang. En sunn voksen kvinne ligger typisk mellom 21 og 33 %, en sunn mann mellom 8 og 24 %.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Hva sier BMI om helsen din?</h2>
            <p>Store epidemiologiske studier — blant annet en metaanalyse i The Lancet (2016) som dekket nesten 4 millioner mennesker — viser en U-formet sammenheng mellom BMI og dødelighet. Lavest risiko ser man typisk mellom BMI 22 og 25 for ikke-røykere. Både svært lav og svært høy BMI er knyttet til høyere dødsrisiko, men det handler ofte om underliggende sykdom snarere enn BMI som årsak i seg selv.</p>
            <p>Det er også viktig å understreke at BMI mellom 25 og 30 — såkalt overvekt — har vist seg i flere studier å være forenlig med god helse forutsatt at midjemål og blodverdier (kolesterol, blodtrykk, fastende blodsukker) er innenfor normalen. Dette kalles ofte "metabolsk sunn overvekt".</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-stone-900 mb-3">Vanlige spørsmål om BMI</h2>
            <div className="space-y-5">
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Er BMI nøyaktig for kvinner?</h3>
                <p>BMI bruker samme grenser for begge kjønn, men kvinner har naturlig høyere kroppsfett-prosent. En kvinne med BMI 24 har typisk mer fett enn en mann med samme BMI. Kombiner derfor BMI med midjemål eller WHtR for en mer presis vurdering.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Hva er en sunn BMI etter alder?</h3>
                <p>Voksne mellom 19 og 64 år har lavest helserisiko i området 18,5–24,9. For personer over 65 år tyder flere studier på at lett "overvekt" (BMI 25–27) er forenlig med bedre prognose, sannsynligvis fordi reservene beskytter ved sykdom. For barn brukes percentiler i stedet for absolutte verdier.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Kan jeg være sunn med høy BMI?</h3>
                <p>Ja, det er fullt mulig. En person med mye muskel, god kondisjon, normalt blodtrykk og normale blodverdier kan ha BMI 28 og likevel være metabolsk sunn. Den motsatte er også sann: en stillesittende person med "normal" BMI 23 kan ha høy fettprosent og økt metabolsk risiko (såkalt "skinny fat").</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Hva er forskjellen på BMI og kroppsfett-prosent?</h3>
                <p>BMI er et matematisk forhold mellom høyde og vekt. Kroppsfett-prosent måler hvor mye av vekten som faktisk er fettvev. To personer kan ha lik BMI med svært ulik kroppsfett-prosent. Kroppsfett-prosent måles best med DEXA-scan, deretter bioimpedansvekt eller hudfoldsmåling.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Hvor ofte bør jeg måle BMI?</h3>
                <p>For voksne med stabil vekt holder det å sjekke 1–2 ganger i året. Ved vektendring eller livsstilsendring kan månedlig oppfølging gi nyttig trend-informasjon — men daglige målinger gir mest støy.</p>
              </div>
              <div>
                <h3 className="font-semibold text-stone-900 mb-1">Hvorfor får jeg ulik BMI på ulike kalkulatorer?</h3>
                <p>Formelen er identisk overalt: vekt delt på høyde i annen. Ulike kalkulatorer kan derimot vise BMI med ulik avrunding (24,5 vs. 24 vs. 24,46). Helsedirektoratet anbefaler å bruke én desimal.</p>
              </div>
            </div>
          </section>

          <section className="border-t border-stone-200 pt-6">
            <h2 className="text-xl font-semibold text-stone-900 mb-3">Kilder</h2>
            <ul className="text-xs text-stone-500 space-y-1.5">
              <li>WHO (2024). <em>Obesity and overweight fact sheet.</em> <a href="https://www.who.int/news-room/fact-sheets/detail/obesity-and-overweight" target="_blank" rel="noopener" className="text-blue-600 hover:underline">who.int</a></li>
              <li>Helsedirektoratet (2020). <em>Forebygging, utredning og behandling av overvekt og fedme hos voksne.</em></li>
              <li>The Global BMI Mortality Collaboration (2016). <em>Body-mass index and all-cause mortality.</em> The Lancet, 388(10046), 776–786.</li>
              <li>Ashwell, M., Gunn, P., & Gibson, S. (2012). <em>Waist-to-height ratio is a better screening tool than waist circumference and BMI.</em> Obesity Reviews, 13(3), 275–286.</li>
              <li>WHO Expert Consultation (2004). <em>Appropriate body-mass index for Asian populations.</em> The Lancet, 363(9403), 157–163.</li>
            </ul>
          </section>
        </article>

        {/* CTA */}
        <EmailCapture category="trening" label="Trening og vekt" />
        <div className="bg-stone-900 text-white rounded-2xl p-8 text-center">
          <div className="text-stone-400 text-xs uppercase tracking-widest mb-3">Neste steg</div>
          <h2 className="font-bold text-2xl mb-2">Tall er bare starten.</h2>
          <p className="text-stone-300 text-base mb-1">En coach bygger <span className="text-white font-semibold">vaner som faktisk holder</span> —</p>
          <p className="text-stone-400 text-sm mb-6">ikke en diett du gir opp etter tre uker.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/trening" className="inline-block px-6 py-3 bg-white text-stone-900 rounded-xl text-sm font-semibold hover:bg-stone-100 transition-colors">Finn personlig trener gratis →</Link>
            <Link href="/kosthold" className="inline-block px-6 py-3 bg-stone-700 text-white rounded-xl text-sm font-medium hover:bg-stone-600 transition-colors">Finn ernæringsfysiolog →</Link>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 pb-12">
          <ShareBar label="Del dette verktøyet" />
        </div>
      </main>
    </div>
  );
}
